import { forwardRef } from 'react';
import type { ReactNode, CSSProperties } from 'react';

import { useCollapse } from '@lib/hooks';
import { cn } from '@lib/utils';

type CollapseProps = {
  children?: ReactNode;
  in: boolean;
  transitionDuration?: number;
  transitionTimingFunction?: CSSProperties['transitionTimingFunction'];
  min?: string;
  onTransitionEnd?: () => void;
} & React.ComponentPropsWithoutRef<'div'>;

const Collapse = forwardRef<HTMLDivElement, CollapseProps>(
  (
    {
      children,
      in: opened,
      transitionDuration,
      transitionTimingFunction,
      min,
      onTransitionEnd,
      className,
      style,
      ...rest
    },
    ref
  ) => {
    const getCollapseProps = useCollapse({
      opened,
      transitionDuration,
      transitionTimingFunction,
      min,
      onTransitionEnd,
    });

    if (transitionDuration === 0) {
      return opened ? (
        <div ref={ref} className={className} style={style} {...rest}>
          {children}
        </div>
      ) : null;
    }

    const collapseProps = getCollapseProps({ style, className, ref, ...rest });

    return (
      <div {...collapseProps} className={cn(className, collapseProps.className as string)}>
        {children}
      </div>
    );
  }
);

Collapse.displayName = 'Collapse';

export { Collapse };
export type { CollapseProps };
